import { Injectable } from '@angular/core';

import { AuthFacadeService } from '@ib-core/services/auth-facade.service';
import { IBalancesResponse } from '@ib-shared/interfaces/balances-response.intf';
import { IDashboardTransactionsResponse } from '@ib-shared/interfaces/dashboard-table-row.intf';
import { IDocumentOption } from '@ib-shared/interfaces/document-option.intf';
import { IPagination } from '@ib-shared/interfaces/pagination.intf';
import { BalancesHttpService } from '@ib-shared/services/balances-http.service';
import { DashboardHttpService } from '@ib-shared/services/dashboard-http.service';
import { RegistrDocTypesService } from '@ib-shared/services/doc-types.service';
import { INVESTOR_DOCUMENT_SUBTYPE } from 'credebt-shared';
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  take,
  tap,
  throwError,
} from 'rxjs';

import { DashboardStateService } from './dashboard-state.service';

@Injectable()
export class DashboardFacadeService {
  constructor(
    private stateS: DashboardStateService,
    private authFacadeS: AuthFacadeService,
    private dashboardHttpS: DashboardHttpService,
    private balancesHttpS: BalancesHttpService,
    private docTypesS: RegistrDocTypesService,
  ) {}

  public fetchBalances(): Observable<IBalancesResponse> {
    this.stateS.balancesLoading$.next(true);
    return this.balancesHttpS.fetchBalances().pipe(
      catchError(err => {
        this.stateS.balancesLoading$.next(false);
        return throwError(() => err);
      }),
      tap(r => {
        this.stateS.balances$.next(r);
        this.stateS.balancesLoading$.next(false);
      }),
    );
  }

  public fetchRows(
    params: IPagination,
  ): Observable<IDashboardTransactionsResponse> {
    this.stateS.tableRowsLoading$.next(true);
    return this.dashboardHttpS.fetchTableRows(params).pipe(
      catchError(err => {
        this.stateS.tableRowsLoading$.next(false);
        return throwError(() => err);
      }),
      tap(r => {
        this.stateS.tableRows$.next(r);
        this.stateS.tableRowsLoading$.next(false);
      }),
    );
  }

  public selectIsTableRowsLoading(): Observable<boolean> {
    return this.stateS.tableRowsLoading$.asObservable();
  }

  public selectRows(): Observable<IDashboardTransactionsResponse> {
    return this.stateS.tableRows$.asObservable();
  }

  public toggleTransferModal(show: boolean): void {
    this.stateS.showTransferModal$.next(show);
  }

  public selectIsTransferModalShow(): Observable<boolean> {
    return this.stateS.showTransferModal$.asObservable();
  }

  public toggleAddDocsModal(show: boolean): void {
    this.stateS.showDocsModal$.next(show);
  }
  public selectShowAddDocsModal(): Observable<boolean> {
    return this.stateS.showDocsModal$.asObservable();
  }

  public initIsAddDocsModalShow(): void {
    if (!this.authFacadeS.isRegistrAdditDocModalWasInited) {
      combineLatest([this.selectBalances(), this.selectRequiredDocuments()])
        .pipe(
          filter(([balances, requiredDocs]) =>
            Boolean(balances && requiredDocs),
          ),
          take(1), // ??? show once
        )
        .subscribe(([balances, requiredDocs]) => {
          const balance = this.getUserBalance(balances);
          const balanceRequirement = balance > 0;
          const showDocs = balanceRequirement && requiredDocs.length > 0;
          this.authFacadeS.registrAdditDocModalWasInited = true;
          this.stateS.showDocsModal$.next(showDocs);
        });
    }
  }

  private getUserBalance(r: IBalancesResponse): number {
    let balance;
    const userCurrency = this.authFacadeS.authUser.bankCurrency;
    Object.entries(r.balance).find(d => {
      if (d[0].toLowerCase() === userCurrency.toLowerCase()) {
        balance = d[1];
        return true;
      }
      return false;
    });
    return balance;
  }

  public setBalances(v: IBalancesResponse): void {
    this.stateS.balances$.next(v);
  }

  public selectBalances(): Observable<IBalancesResponse> {
    return this.stateS.balances$.asObservable();
  }

  public initShowTransferMoneyModal(): void {
    combineLatest([this.selectBalances(), this.authFacadeS.selectAuthUser()])
      .pipe(
        filter(([balances, authUser]) => Boolean(balances && authUser)),
        take(1), // ??? show once !!! replace from module
      )
      .subscribe(([balances]) => {
        const balance = this.getUserBalance(balances);
        const show = balance <= 0;
        this.stateS.showTransferModal$.next(show);
      });
  }

  public selectRequiredDocuments(): Observable<Array<IDocumentOption>> {
    return this.authFacadeS.selectAuthUser().pipe(
      filter(r => Boolean(r)),
      map(authUser => {
        const requiredSubTypes: Array<INVESTOR_DOCUMENT_SUBTYPE> = [
          !authUser.registrDocs?.find(
            i => i.subType === INVESTOR_DOCUMENT_SUBTYPE.ID_DOC,
          )
            ? INVESTOR_DOCUMENT_SUBTYPE.ID_DOC
            : null,
          !authUser.registrDocs?.find(
            i => i.subType === INVESTOR_DOCUMENT_SUBTYPE.TAX_DOC,
          )
            ? INVESTOR_DOCUMENT_SUBTYPE.TAX_DOC
            : null,
        ].filter(r => r);
        return this.docTypesS.DOCUMENTS_OPTIONS.filter(i => {
          return (
            i.accType.includes(authUser.accountType) &&
            requiredSubTypes.includes(i.subType)
          );
        });
      }),
    );
  }
}
